import { FC, useMemo, useState, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { Close } from '@mui/icons-material';
import {
  ButtonBase,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Product, currencies } from '@vs-online-only-app/state';
import { formatPriceWithCurrency, Link } from '@vs-online-only-app/ui';
import { IconImageMissingSvg } from '../assets/icons';

type WrapperProps = {
  $noBorder?: boolean;
  $noPadding?: boolean;
  $thumbnailOnly?: boolean;
};

const Wrapper = styled(ButtonBase)<WrapperProps>`
  display: grid;
  grid-template-columns: auto;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;
  max-width: 290px;
  position: relative;
  text-align: left;

  ${({ $thumbnailOnly }) =>
    !$thumbnailOnly &&
    css`
      grid-template-columns: auto 1fr auto;
      min-width: 200px;
    `}

  ${({ $noPadding, theme }) =>
    !$noPadding &&
    css`
      padding: ${theme.spacing(1)};
    `}

  ${({ $noBorder, theme }) =>
    !$noBorder &&
    css`
      border: 1px solid ${theme.palette.divider};
    `}
`;

export enum ThumbnailSize {
  Small = 'small',
  Regular = 'regular',
  Large = 'large',
  ExtraLarge = 'extraLarge',
}
const thumbnailSizes = {
  [ThumbnailSize.Small]: 50,
  [ThumbnailSize.Regular]: 80,
  [ThumbnailSize.Large]: 96,
  [ThumbnailSize.ExtraLarge]: 140,
};

const ProductThumbnailWrapper = styled.div<{ $thumbnailSize: ThumbnailSize }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  height: ${({ $thumbnailSize }) => thumbnailSizes[$thumbnailSize]}px;
  width: ${({ $thumbnailSize }) => thumbnailSizes[$thumbnailSize]}px;
  overflow: hidden;
`;

const ProductThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FallbackThumbnail = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.palette.background.default};
  object-fit: scale-down;
`;

const OverflowText = styled(Typography)<{ $long?: boolean }>`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: ${({ $long }) => ($long ? '185px' : '110px')};
`;

const RemoveButton = styled(IconButton)`
  margin-bottom: auto;
`;

const parseCurrency = (currency: keyof typeof currencies, price: number) => {
  const parsedPrice = parseFloat(String(price)).toFixed(2).replace('.', ',');
  return currencies[currency]
    ? `${currencies[currency]}${parsedPrice}`
    : `${parsedPrice} ${currency}`;
};

type Props = {
  product: Product;
  thumbnailOnly?: boolean;
  thumbnailSize: ThumbnailSize;
  long?: boolean;
  noBorder?: boolean;
  noPadding?: boolean;
  onRemove?: (product: Product) => void;
};

const ProductCard: FC<Props> = ({
  product,
  thumbnailSize,
  thumbnailOnly,
  long,
  noBorder,
  noPadding,
  onRemove,
}) => {
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState<boolean>(false);

  const formattedSalePrice = useMemo(
    () =>
      product.salePrice
        ? formatPriceWithCurrency({ price: product.salePrice, currency: product.currency })
        : null,
    [product.salePrice, product.currency],
  );

  const formattedPrice = useMemo(
    () => product.price && formatPriceWithCurrency({ price: product.price, currency: product.currency }),
    [product.price, product.currency],
  );

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRemove) onRemove(product);
  };

  const isSmall = thumbnailSize === ThumbnailSize.Small;

  return (
    <Link
      to={product.url}
      underline="none"
      sx={{ width: 'fit-content' }}
      target="_blank"
    >
      <Wrapper
        $noBorder={noBorder}
        $noPadding={noPadding || thumbnailOnly}
        $thumbnailOnly={thumbnailOnly}
        as="span"
      >
        <Stack gap={0.5}>
          <ProductThumbnailWrapper $thumbnailSize={thumbnailSize}>
            {product.thumbnail && (
              <ProductThumbnail
                src={product.thumbnail}
                onLoad={() => setIsThumbnailLoaded(true)}
              />
            )}
            {(!product.thumbnail || !isThumbnailLoaded) && (
              <FallbackThumbnail src={IconImageMissingSvg} />
            )}
          </ProductThumbnailWrapper>
        </Stack>
        {!thumbnailOnly && (
          <>
            <Stack gap={isSmall ? 0 : 0.5}>
              <OverflowText
                variant={isSmall ? 'caption' : 'body2'}
                fontWeight={500}
                $long={long}
              >
                {product.title}
              </OverflowText>
              <Stack direction="row" gap={0.5}>
                {product.salePrice ? (
                  <>
                    <Typography
                      variant={isSmall ? 'caption' : 'body2'}
                      color="error"
                    >
                      {formattedSalePrice}
                    </Typography>
                    {formattedPrice && (
                      <Typography
                        variant={isSmall ? 'caption' : 'body2'}
                        sx={{ textDecoration: 'line-through' }}
                        color="textSecondary"
                      >
                        {formattedPrice}
                      </Typography>
                    )}
                  </>
                ) : (
                  <OverflowText variant={isSmall ? 'caption' : 'body2'}>
                    {formattedPrice}
                  </OverflowText>
                )}
              </Stack>
            </Stack>
            {onRemove && (
              <Tooltip arrow title="Remove">
                <RemoveButton onClick={handleRemove} size="small">
                  <Close fontSize="small" />
                </RemoveButton>
              </Tooltip>
            )}
          </>
        )}
      </Wrapper>
    </Link>
  );
};

export default ProductCard;
